import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import './poster-mobile-big-seekbar.scss';
import {MediaControlButtons} from '@Components/media-control-buttons';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Icon} from '@Components/icon-v2';
import {CustomDurationSlider} from '@Components/poster-editor/components/poster-seekbar/components/custom-duration-slider';
import {doesPosterHaveAudios, onPosterPause, onPosterPlay, onPosterSkipForward, onPosterStop} from '@Components/poster-editor/library/poster-editor-library';
import {Text, TextSize} from '@Components/text';
import {convertSecondsToMMSSFormat} from '@Utils/date.util';
import {CurrentTimeText} from '@Components/poster-editor/components/poster-seekbar/components/current-time-text';
import {getCurrentPageDuration, getPosterDuration, setSelectedAudioItemUID, updateIsTimelineModeActive} from '@Components/poster-editor/poster-editor-reducer';
import {POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT} from '@Components/poster-editor/components/poster-mobile-big-seekbar/poster-mobile-big-seekbar.types';
import {MODALS_IDS} from '@Components/modal-container';
import Divider from '@Components/divider';
import {PosterEditorMobileTimeline} from '@Components/poster-editor/components/poster-editor-mobile-timeline/poster-editor-mobile-timeline';
import {openTimelineModal} from '@Modals/timeline-modal/timeline-modal';
import {fireGAEventForTimelineOpen} from '@Components/poster-editor/components/poster-editor-web-bottom-bar/poster-editor-web-bottom-bar.helper';
import styles from './poster-mobile-big-seekbar.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {closeAllModals} from '@Components/modal-container/modal-container-reducer';

interface PosterMobileBigSeekbarProps {
  setVisibiltyHidden?: boolean;
}

export function PosterMobileBigSeekbar({setVisibiltyHidden = false}: PosterMobileBigSeekbarProps): ReactElement<any> | null {
  const isAudioPlaylistSelected = useAppSelector((state) => {
    return state.posterEditor.posterObject?.audioClips.selectedAudioItemUID !== '';
  });
  const isTimelineModeActive = useAppSelector((state) => {
    return state.posterEditor.isTimelineModeActive;
  });

  const isPlaying = useAppSelector((state) => {
    return state.posterEditor.isPlaying;
  });
  const currentTime = useAppSelector((state) => {
    return state.posterEditor.currentTime;
  });
  const posterDuration = useAppSelector(getPosterDuration);
  const isAudioModalOpen =
    useAppSelector((state) => {
      return state.modals.modalsHashmap[MODALS_IDS.EDIT_AUDIO_ITEM_MODAL]?.isOpen;
    }) || false;
  const hasMultiplePages = window.posterEditor?.whiteboard?.pages.hasMultiplePages();
  // TODO: Hamza has converted page from an array to Object. Ask him about retrieving page number
  const currentPageNumber = 1;
  const durationSliderContainerClassName = 'pageDurationSlider';
  const currentPageDuration = useAppSelector(getCurrentPageDuration);
  const dispatch = useAppDispatch();

  useEffect((): void => {
    if (!isAudioModalOpen && isAudioPlaylistSelected) {
      dispatch(setSelectedAudioItemUID(''));
    }
  }, [isAudioModalOpen]);

  const getPageInfoOptions = (): ReactElement<any> | null => {
    return hasMultiplePages ? (
      <div className={`${styles.pageInfo} spacing-p-t-2 spacing-p-b-2 spacing-p-r-4 spacing-p-l-4`}>
        <span className={`${styles.pageNumberText} body-xxs-bold`}>{`Page ${currentPageNumber}`}</span>
      </div>
    ) : null;
  };

  const getCustomDurationSlider = (): ReactElement<any> => {
    return (
      <div className={styles.seekbarSliderContainerMobile}>
        <CustomDurationSlider className={durationSliderContainerClassName} />
      </div>
    );
  };

  const getTotalTime = (): ReactElement<any> => {
    return <Text val={convertSecondsToMMSSFormat(currentPageDuration)} size={TextSize.XXSMALL} />;
  };

  const getCurrentTime = (): ReactElement<any> => {
    return <CurrentTimeText />;
  };

  const onClickHandler = (): void => {
    dispatch(closeAllModals());
    dispatch(updateIsTimelineModeActive(true));
    fireGAEventForTimelineOpen();
    openTimelineModal();
  };

  const getMobileSeekbarMainPanel = (): ReactElement<any> => {
    return (
      <div className={`${styles.seekbarViewMobile} _full-width flex-v-row`}>
        {getCustomDurationSlider()}

        <div className="flex-justify-between">
          <MediaControlButtons
            isMobileView
            currentTime={currentTime}
            totalDuration={posterDuration}
            isPlaying={isPlaying}
            onPause={onPosterPause}
            onPlay={onPosterPlay}
            onStop={onPosterStop}
            showSkipForwardButton={hasMultiplePages}
            onSkipForward={onPosterSkipForward}
            iconSize={IconSize.SIZE_ICON_20}
          />
          <div
            onClick={onClickHandler}
            className={`${styles.durationDetailsContainer} _fit-width hover-transition-all spacing-p-l-3 spacing-p-r-3 flex-h-row flex-align-items-center radius-round border-s-standard`}
          >
            {doesPosterHaveAudios() ? (
              <>
                <Icon icon="icon-audio" size={IconSize.SIZE_ICON_16} className="spacing-p-l-1 spacing-p-r-4" />
                <Divider width={1} height={20} additionalClasses="spacing-m-r-2" />
              </>
            ) : null}
            {getCurrentTime()}
            {getTotalTime()}
            <Icon icon="icon-pencil" size={IconSize.SIZE_ICON_16} className="spacing-m-l-1 spacing-m-r-1" />
          </div>
        </div>
      </div>
    );
  };

  const getTimelineForMobile = (): ReactElement<any> => {
    return <PosterEditorMobileTimeline />;
  };

  const getBigMobileSeekbar = (): ReactElement<any> => {
    if (isTimelineModeActive) {
      return getTimelineForMobile();
    }

    return (
      <div
        className={`${isAudioPlaylistSelected ? styles.fullwidthSeekbarWrapperMovedUp : styles.fullwidthSeekbarWrapper} ${setVisibiltyHidden ? styles.visibiltyHidden : ''} ${
          styles.seekbar
        } _full-width flex-v-row flex-items-center _fit-height`}
        style={{
          height: `${POSTER_MOBILE_BIG_SEEK_BAR_HEIGHT}px`,
        }}
      >
        <div className={`js-hide flex-center spacing-p-t-3 ${styles.mobileSeekbar}`}>
          {getPageInfoOptions()}
          {getMobileSeekbarMainPanel()}
        </div>
      </div>
    );
  };

  return getBigMobileSeekbar();
}
